import React, { useEffect } from "react";
import Modal from "../common/modal";
import Button from "../common/button";
import FallbackImage from "../common/fallbackImage";
import { useEnquiryContext } from "../../context/EnquiryContext";
import { useViewSingleUser } from "../../hooks/users/viewSingleUser";

/* ─────────────────────────────────────────
   STATUS BADGE
───────────────────────────────────────── */
const STATUS_STYLES = {
  pending:  { bg: "#fef9c3", color: "#854d0e", border: "#fde047" },
  closed:   { bg: "#fee2e2", color: "#991b1b", border: "#fca5a5" },
};

const StatusBadge = ({ status }) => {
  const key   = String(status || "pending").toLowerCase();
  const style = STATUS_STYLES[key] || { bg: "#f3f4f6", color: "#374151", border: "#d1d5db" };
  return (
    <span style={{
      display: "inline-block", padding: "3px 12px", borderRadius: "999px",
      fontSize: "12px", fontWeight: 600, background: style.bg,
      color: style.color, border: `1px solid ${style.border}`, textTransform: "capitalize",
      width:"max-content", 
    }}>
      {key}
    </span>
  );
};

/* ─────────────────────────────────────────
   FIELD
───────────────────────────────────────── */
const Field = ({ label, value, fullWidth = false, children }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 3, gridColumn: fullWidth ? "1 / -1" : undefined }}>
    <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#9ca3af" }}>
      {label}
    </span>
    {children ?? (
      <span style={{ fontSize: 14, color: value ? "#111827" : "#9ca3af", wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
        {value || "—"}
      </span>
    )}
  </div>
);

/* ─────────────────────────────────────────
   HELPERS  — uses actual field names from API debug
   salesUserId: { _id, name, email, phone, designation }
   client:      { _id, name, email, phone }
   products:    []   (array of product objects)
   location:    "mumbai"  (string)
   description: "new enquiry"
───────────────────────────────────────── */
const getSalesUserId = (enquiry) =>
  enquiry?.salesUserId?._id ||
  enquiry?.salesUserId?.id  ||
  (typeof enquiry?.salesUserId === "string" ? enquiry.salesUserId : null);

const getSalesUserName = (enquiry) =>
  enquiry?.salesUserId?.name  ||
  enquiry?.salesUserId?.email ||
  enquiry?.name               ||
  "—";

const getSalesUserEmail = (enquiry) =>
  enquiry?.salesUserId?.email ||
  enquiry?.email              ||
  "—";

const getClientName = (enquiry) => {
  const c = enquiry?.client;
  if (!c) return enquiry?.clientName || "—";
  if (typeof c === "string") return c;
  return c.name || c.companyName || "—";
};

const getProducts = (enquiry) => {
  const p = enquiry?.products || enquiry?.product;
  if (!p) return "—";
  const arr = Array.isArray(p) ? p : [p];
  if (!arr.length) return "—";
  const names = arr
    .map((item) => (typeof item === "string" ? item : item?.title || item?.name || item?._id))
    .filter(Boolean);
  return names.length ? names.join(", ") : "—";
};

const getLocation = (enquiry) => {
  const loc = enquiry?.location;
  if (!loc) return enquiry?.country || enquiry?.city || "—";
  if (typeof loc === "string") return loc;
  return [loc.city, loc.state, loc.country].filter(Boolean).join(", ") || "—";
};

const formatDate = (val) =>
  val
    ? new Date(val).toLocaleString("en-GB", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      })
    : "—";

/* ─────────────────────────────────────────
   SALES USER AVATAR — fetches from /user/:id
───────────────────────────────────────── */
const SalesUserAvatar = ({ userId, name, size = 52 }) => {
  const { data } = useViewSingleUser(userId);

  const user =
    data?.data?.user ||
    data?.data       ||
    data?.user       ||
    data             ||
    null;

  const src =
    user?.profilePicture     ||
    user?.profilePicturePath ||
    user?.avatar             ||
    null;

  const initial = String(name || "?").trim().charAt(0).toUpperCase();

  if (src) {
    return (
      <FallbackImage
        src={src}
        alt={name}
        style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
      />
    );
  }

  return (
    <span style={{
      width: size, height: size, borderRadius: "50%",
      background: "#ede9fe", color: "#5c308d",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontWeight: 800, fontSize: size * 0.38, flexShrink: 0,
    }}>
      {initial}
    </span>
  );
};

/* ─────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────── */
const ViewEnquiryModal = ({ isOpen, onClose, enquiry }) => {
  const { markAsRead } = useEnquiryContext();

  // Mark enquiry as read when modal opens
  useEffect(() => {
    if (isOpen && enquiry) {
      markAsRead(enquiry._id || enquiry.id);
    }
  }, [isOpen, enquiry, markAsRead]);

  if (!enquiry) return null;

  const salesUserId   = getSalesUserId(enquiry);
  const salesUserName = getSalesUserName(enquiry);
  const salesEmail    = getSalesUserEmail(enquiry);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Enquiry Details">
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

        {/* ── HEADER CARD: avatar + name + email + status ── */}
        <div style={{
          display: "flex", alignItems: "flex-start", gap: 14,
           borderRadius: 10,
          background: "#f9fafb", border: "1px solid #f3f4f6",
          flexDirection: "column",
        }}>
          <SalesUserAvatar userId={salesUserId} name={salesUserName} size={52} />

          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontWeight: 700, fontSize: 15, color: "#111827", textAlign: "left" }}>
              {salesUserName}
            </p>
            <p style={{ margin: "2px 0 0", fontSize: 13, color: "#6b7280" }}>
              {salesEmail}
            </p>
          </div>

          <StatusBadge status={enquiry.status} />
        </div>

        {/* ── DETAIL GRID ── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px 24px", textAlign: "left" }}>

          <Field label="Sales User Name"  value={salesUserName} />
          <Field label="Client Name"      value={getClientName(enquiry)} />
          <Field label="Products"         value={getProducts(enquiry)} />
          <Field label="Location"         value={getLocation(enquiry)} />
          <Field label="Sales User Email" value={salesEmail} />
          <Field label="Date Received"    value={formatDate(enquiry.createdAt)} />
          <Field label="Status">
            <StatusBadge status={enquiry.status} />
          </Field>
          <Field label="Last Updated"     value={formatDate(enquiry.updatedAt)} />

          <Field
            label="Description / Message"
            value={enquiry.description || enquiry.message || enquiry.subject}
            fullWidth
          />
        </div>
      </div>

      {/* FOOTER */}
      <div className="modal-footer" style={{ marginTop: 8 }}>
        <Button className="CommonButton" onClick={onClose}>
          Close
        </Button>
      </div>
    </Modal>
  );
};

export default ViewEnquiryModal;
